import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Observable } from 'rxjs';

import { Place } from '@app/modules/model/place';
import { Audit } from '@app/modules/model/audit';
import { CashChange } from '@app/modules/model/cashchange';
import { TaxBreakdown } from '@app/modules/model/ticket';

import { languageService, languageSupport } from '@app/modules/common/language';
import { printService } from '@app/modules/print';
import { viewService } from '@app/modules/view';

/************************************/
/* AUDIT TICKET PRINT               */
/************************************/

@Component({
    selector: 'print-audit',
    templateUrl:'./ui-print.html',
    styleUrls: [ 
        'ui-print.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAuditPrint extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('place') _place: Place = null;
    @Input('audit') _audit: Audit = null;
    @Input('print') _print: Observable <any> = null;

    // True once the audit data needed for printing (tickets, series ranges)
    // has finished loading. Parents (audit modal) gate the print button on
    // this so the user can't print a half-loaded report.
    @Output('onReady') _onReady = new EventEmitter<boolean>();
    private _isready = false;
    get IsReady(){
        return this._isready;
    }
    private _setReady(value: boolean){
        if (this._isready !== value){
            this._isready = value;
            this._onReady.emit(value);
            this.change.markForCheck();
        }
    }
    
    // The previous-audit resolution lives in Audit.Previous (date + till
    // based). Cache it here by (audit reference, place.audits.length): the
    // getter is read from inside Report, which is invalidated on every
    // audit OnRefresh tick, so an uncached lookup over tens of thousands
    // of historical audits would saturate the main thread. The cache
    // invalidates whenever a new audit is added or the @Input audit changes.
    private _previous_cache: { audit: Audit, length: number, value: Audit } = null;
    private get _previous() : Audit{
        if (!this._audit || !this._place){
            return null;
        }

        let _placeaudits = this._place.audits;
        let _cache = this._previous_cache;
        if (_cache && _cache.audit === this._audit && _cache.length === _placeaudits.length){
            return _cache.value;
        }

        let _result = this._audit.Previous;

        this._previous_cache = { audit: this._audit, length: _placeaudits.length, value: _result };
        return _result;
    }

    // The IVA breakdown is computed server-side (auditinfo.php) and stored
    // on the auditinfo, so the report reads it straight from there instead
    // of recomputing it from ticket lines — that recompute came out short
    // for older cierres whose tickets load without their lines.
    get TaxBreakdown(): TaxBreakdown {
        let _auditinfo = this._audit ? this._audit.auditinfo : null;
        return _auditinfo ? _auditinfo.taxbreakdown : [];
    }

    // cached so the template doesn't get a brand-new object on every CD cycle
    // (two reads per cycle through .base + .taxes would otherwise allocate
    // garbage and could even trip ExpressionChangedAfterChecked under load)
    private _taxtotals_cache: { source: any, value: { base: number, taxes: number } } = null;
    get TaxTotals(){
        let _breakdown = this.TaxBreakdown;
        if (this._taxtotals_cache && this._taxtotals_cache.source === _breakdown){
            return this._taxtotals_cache.value;
        }

        let _base = 0;
        let _taxes = 0;
        for (let _entry of _breakdown){
            _base  += _entry.base;
            _taxes += _entry.taxes;
        }

        let _value = {
            base:  Math.round(_base  * 100) / 100,
            taxes: Math.round(_taxes * 100) / 100
        };
        this._taxtotals_cache = { source: _breakdown, value: _value };
        return _value;
    }

    private _seriesranges: Array <{ series: string, first: string, last: string, count: number }> = [];
    get SeriesRanges(){
        return this._seriesranges;
    }

    private _report = null;
    get Report() {
        if (this._report == null){
            let _previous = this._previous;

            let _report = {
                last: null,
                next: null,
                user: null,
                till: null,
                cash: 0,
                card: 0,
                webpay: 0,
                inout: 0,
                sales: 0,
                total: 0,
                changes: [],
                tickets: {
                    amount_completed: 0,
                    amount_cancelled: 0
                },
                products: {
                    amount_completed: 0,
                    income_completed: 0,
                    amount_cancelled: 0,
                    income_cancelled: 0,
                    amount_onthehouse: 0,
                    income_onthehouse: 0
                }
            };
    
            // audit dates: from .. to
            _report.next = this._audit.created;
            if (_previous){
                _report.last = _previous.created;
            }
    
            // audit user
            if (this._audit.session?.user){
                _report.user = this._audit.session.user.fullname;
            }
    
            // audit till
            if (this._audit.till){
                _report.till = this._audit.till?.name || this._lang.tr('@place_till_noname');
            }

            // audit cash in till
            _report.inout = this._audit.cash;
    
            // audit payments
            let _auditinfo = this._audit.auditinfo;
            if (_auditinfo){
                _report.cash =_auditinfo.cash;
                _report.card = _auditinfo.card;
                _report.webpay = _auditinfo.webpay;
            }
    
            _report.sales = _report.cash + _report.card + _report.webpay;
            _report.sales = Math.round(_report.sales * 100) / 100;
            _report.total = _report.inout + _report.cash;
            _report.total = Math.round(_report.total * 100) / 100;
    
            _report.changes = this._audit.cashchanges;
    
            // tickets and products
            if (_auditinfo){
                _report.tickets = {
                    amount_completed: _auditinfo.tickets.completed_amount,
                    amount_cancelled: _auditinfo.tickets.cancelled_amount
                };
    
                _report.products = {
                    amount_completed: _auditinfo.items.completed_amount,
                    income_completed: _auditinfo.items.completed_income,
                    amount_cancelled: _auditinfo.items.cancelled_amount,
                    income_cancelled: _auditinfo.items.cancelled_income,
                    amount_onthehouse: _auditinfo.items.othehouse_amount,
                    income_onthehouse: _auditinfo.items.othehouse_income
                };
            }
    
            _report.products.income_cancelled *= -1;    // positive value means money lost
            _report.products.income_onthehouse *= -1;   // positive value means money lost

            this._report = _report;
        }

        return this._report;
    }

    /**************************/
    /* STARTS HERE            */
    /**************************/

    private OnRefreshReport(){
        this._report = null;
        this.change.markForCheck();
    }

    private _seriesranges_seq = 0;
    private async _RefreshSeriesRanges(){
        if (!this._audit || !this._audit.IsLoaded){
            return;
        }

        let _audit = this._audit;
        let _seq = ++this._seriesranges_seq;

        let _result: Array <{ series: string, first: string, last: string, count: number }> = [];

        // Prefer the server-computed ranges (auditinfo/recalculate.php),
        // filtered by the cierre's till — the modal fetches them on open.
        // Recomputing locally (TicketSeriesRange) uses the device's own
        // till/memory, so a TPV printed another TPV's cierre with its own
        // series. Fallback to the local recompute only when the server
        // ranges are not available yet (offline / not fetched).
        let _server = _audit.auditinfo?.ServerSeriesRanges;
        if (Array.isArray(_server)){
            _result = _server;
        }
        else {
            try {
                _result = await _audit.TicketSeriesRange();
            }
            catch (err) {
                console.warn('[AUDIT-PRINT] Failed to load ticket series ranges', err);
            }
        }

        if ((_seq !== this._seriesranges_seq) || (_audit !== this._audit)){
            return;
        }

        this._seriesranges = _result;
        this._setReady(true);
        this.change.markForCheck();
    }

    // Adopt the server-computed series ranges when they arrive: the modal's
    // recalculate.php fetch is async, so it may set them after the first
    // _RefreshSeriesRanges() already ran with the local fallback.
    private _AdoptServerSeriesRanges(){
        let _server = this._audit?.auditinfo?.ServerSeriesRanges;
        if (Array.isArray(_server) && (_server !== this._seriesranges)){
            this._seriesranges = _server;
            this._setReady(true);
            this.change.markForCheck();
        }
    }

    constructor(lang: languageService, private change: ChangeDetectorRef, private printer: printService, public view: viewService) {
        super(lang, change);
    }
    
    private _audit_subscription = null;
    private _print_subscription = null;

    ngOnInit(){
        this._print_subscription = this._print.subscribe(
        data => {
            this.PrintAudit();
        });
    }

    // ngOnChanges fires more often than the audit input actually changes:
    // tab-cash passes [print]="OnPrint" where OnPrint is a getter that runs
    // asObservable() every CD, returning a new Observable each time. Without
    // this guard, every CD on tab-cash would re-subscribe and re-trigger the
    // heavy refreshes here, then those refreshes would markForCheck → another
    // CD on tab-cash → another new Observable from OnPrint → infinite loop.
    // We only redo the work when the actual audit reference changes.
    private _last_audit_seen: Audit = null;
    private _heavy_refresh_done = false;

    ngOnChanges(){
        if (this._last_audit_seen === this._audit){
            return;        // nothing material changed, skip the resubscribe loop
        }
        this._last_audit_seen = this._audit;
        this._heavy_refresh_done = false;
        this._setReady(false);

        if (this._audit_subscription != null){
            this._audit_subscription.unsubscribe();
            this._audit_subscription = null;
        }

        this._audit_subscription = this._audit.OnRefresh.subscribe(
        data => {
            this.OnRefreshReport();

            // first OnRefresh after audit becomes loaded triggers the heavy
            // refreshes; subsequent emissions only invalidate the Report cache
            if (!this._heavy_refresh_done && this._audit?.IsLoaded){
                this._heavy_refresh_done = true;
                this._RefreshSeriesRanges();
            }
            else if (this._audit?.IsLoaded){
                // server ranges (recalculate.php) may arrive after the first
                // refresh — adopt them without re-running the local recompute.
                this._AdoptServerSeriesRanges();
            }
        });

        if (this._audit?.IsLoaded){
            this._heavy_refresh_done = true;
            this._RefreshSeriesRanges();
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._audit_subscription != null){
            this._audit_subscription.unsubscribe();
            this._audit_subscription = null;
        }

        if (this._print_subscription){
            this._print_subscription.unsubscribe();
            this._print_subscription = null;
        }
    }

    /************************/
    /* EVENTS HANDLER       */
    /************************/

    CashAmount(cashchange: CashChange){
        return Math.abs(cashchange.amount);
    }

    CashDoneBy(cashchange: CashChange){
        if (cashchange.session && cashchange.session.user){
            return cashchange.session.user.fullname;
        }
        return null;
    }

    @ViewChild('printable', {static: false} ) _printable: ElementRef;
    private PrintAudit(){
        let _device = null;

        if (this._place.till){
            for(let _printer of this._place.till.printers){
                if (!_device && (_printer.type == 'BAR')) {
                    _device = _printer;
                }
            }                
        }

        // send the ticket to the device
        if (this._printable){
            this.printer.PrintAudit(this._place, _device, this._printable.nativeElement);
        }
    }
}
